/**
 * Session 68 — turn ANY server error payload into a plain string.
 *
 * FastAPI returns `detail` as a string for HTTPException, but as an ARRAY
 * of `{type, loc, msg, input, ctx, url}` objects for 422 validation
 * errors. Rendering that array straight into a toast / <p> threw
 * "Minified React error #31 (Objects are not valid as a React child)" on
 * the Kitchen login screen in production. Everything that shows a server
 * message should go through here.
 */

const FIELD_LABELS = {
  password: "password",
  pin: "PIN",
  business_slug: "business code",
  slug: "business code",
  table_number: "table name",
  quantity: "quantity",
  amount: "amount",
};

function fieldName(loc) {
  if (!Array.isArray(loc) || !loc.length) return "";
  const last = loc[loc.length - 1];
  if (typeof last === "number") return "";
  const key = String(last || "");
  return FIELD_LABELS[key] || key.replace(/_/g, " ");
}

export function toText(value, fallback = "") {
  if (value === null || value === undefined) return fallback;
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (value instanceof Error) return value.message || fallback;
  if (Array.isArray(value)) {
    const parts = value.map((v) => toText(v)).filter(Boolean);
    return parts.length ? parts.join(" · ") : fallback;
  }
  if (typeof value === "object") {
    // Pydantic validation item
    if (typeof value.msg === "string") {
      const field = fieldName(value.loc);
      const msg = value.msg.replace(/^Value error,\s*/i, "");
      return field ? `${field}: ${msg}` : msg;
    }
    if (value.detail !== undefined) return toText(value.detail, fallback);
    if (typeof value.message === "string") return value.message;
    if (typeof value.error === "string") return value.error;
    try {
      return JSON.stringify(value).slice(0, 300);
    } catch (_) {
      return fallback;
    }
  }
  return fallback;
}

/** Friendly text for an axios/fetch error. */
export function apiErrorMessage(error, fallback = "Something went wrong — please try again.") {
  if (!error) return fallback;
  const status = error?.response?.status;
  const detail = error?.response?.data?.detail;
  if (detail !== undefined && detail !== null) {
    const text = toText(detail, "");
    if (text) return status === 422 ? `Please check: ${text}` : text;
  }
  if (!status && error?.message && /network|timeout|failed to fetch/i.test(error.message)) {
    return "Connection error — check your internet and try again.";
  }
  if (status === 422) return "Please check what you typed and try again.";
  return toText(error?.response?.data?.message, "") || fallback;
}
